import axiosLib from 'axios';
import rollbarServer, { createRollbarInstance } from './rollbar';

import { getCookie } from 'cookies-next';
import { ORGANIZATION, USER_TOKEN } from './storageKeys';
import { shouldSkipLoggingForAuthError } from './error';
import { env } from '@/env';

import { cleanUserLocalState } from './user';

const axios = axiosLib.create({
  baseURL: env.NEXT_PUBLIC_SERVER_URL,
});

export const setAxiosAuthentication = (authenticationToken?: string | null) => {
  axios.defaults.headers.common['Authorization'] = authenticationToken
    ? `Bearer ${authenticationToken}`
    : ''; // Clean header
  axios.defaults.headers.common['Organization-Id'] = '668e9aacb62d6e240847377f'; // Harding-coding for now
};

export const setAxiosOrganizationId = (organizationId?: string | null) => {
  axios.defaults.headers.common['Organization-Id'] = organizationId;
};

// Error handling
axios.interceptors.response.use(undefined, (error) => {
  // console.log('Error', error);

  // Check if error has response (HTTP error) vs network error
  if (error.response) {
    // HTTP error (server responded with error status)
    if (
      error.response.status === 401 &&
      typeof window !== 'undefined' &&
      window.location.pathname !== '/entrar' &&
      window.location.pathname !== '/primeiro-acesso' &&
      window.location.pathname !== '/registrar-senha'
    ) {
      cleanUserLocalState();

      const path = '/';

      fetch(`/api/revalidate?path=${encodeURIComponent(path)}&layout=true`).then(() => {
        rollbarServer.info('User was logged out', { error, path: window.location.pathname });
        window.location.href = '/entrar';
      });
    }
  } else if (error.request) {
    // Network error (no response received - backend offline, timeout, etc.)
    console.log('Network error 👉 backend may be offline', error.message);
  } else {
    // Request setup error
    console.log('Request setup error', error.message);
  }

  if (shouldSkipLoggingForAuthError(error)) {
    // Auth error that should not log to rollbar
    return Promise.reject(error);
  }

  if (typeof window === 'undefined') {
    // Server error
    rollbarServer.error('Error on axios response - server', {
      error,
      errorType: error.response
        ? 'HTTP_ERROR'
        : error.request
          ? 'NETWORK_ERROR'
          : 'REQUEST_SETUP_ERROR',
    });
  } else {
    // Client error
    const rollbarClient = createRollbarInstance(true);
    rollbarClient?.error('Error on axios response - client', {
      error,
      errorType: error.response
        ? 'HTTP_ERROR'
        : error.request
          ? 'NETWORK_ERROR'
          : 'REQUEST_SETUP_ERROR',
    });
  }

  return Promise.reject(error);
});

// Authentication
axios.interceptors.request.use(
  (config) => {
    if (!config.headers?.['Authorization']) {
      // Token is not defined, trying to redefine it
      const token = getCookie(USER_TOKEN);
      // Locally defining token for the ongoing request
      if (!token) {
        if (typeof window === 'undefined') {
          // Server error
          rollbarServer.error('Axios request sem Authorization header', {
            url: config.url,
            method: config.method,
            headers: config.headers,
            cookies: {
              USER_TOKEN: token,
              ORGANIZATION: getCookie(ORGANIZATION),
            },
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
          });
        } else {
          // Client error
          const rollbarClient = createRollbarInstance(true);
          rollbarClient?.error('Axios request sem Authorization header', {
            url: config.url,
            method: config.method,
            headers: config.headers,
            cookies: {
              USER_TOKEN: token,
              ORGANIZATION: getCookie(ORGANIZATION),
            },
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
          });
        }
      }
      if (config.headers) config.headers['Authorization'] = token ? `Bearer ${token}` : '';
      // Setting default headers for the next requests
      setAxiosAuthentication(token);
    }

    const org = getCookie(ORGANIZATION);
    const orgId = JSON.parse(org || '{}').id as string | undefined;
    if (
      orgId &&
      (!config.headers?.['Organization-Id'] || config.headers['Organization-Id'] !== orgId)
    ) {
      config.headers['Organization-Id'] = orgId;
      setAxiosOrganizationId(orgId);
    }

    // Do something before request is sent
    return config;
  },
  (error) => {
    // Do something with request error
    return Promise.reject(error);
  },
);

export default axios;
